import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, X, Camera, Image, FileText, Mic, Plus, MicOff } from "lucide-react";

/**
 * NexiComposer - Chat input with proper mobile support
 * Fixed: Text visibility, cursor, mobile keyboard, voice integration
 */
const NexiComposer = ({
  value,
  onChange,
  onSend,
  onFileSelect,
  onImageSelect,
  onCameraSelect,
  onVoiceClick,
  isLoading,
  disabled,
  selectedFile,
  imagePreview,
  onRemoveFile,
  onRemoveImage,
  voiceState = "idle",
  voiceSupported = true,
  placeholder = "Ask Nexi anything..."
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const newHeight = Math.min(textareaRef.current.scrollHeight, 120);
      textareaRef.current.style.height = newHeight + "px";
    }
  }, [value]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && !isLoading && (value.trim() || selectedFile || imagePreview)) {
        onSend();
      }
    }
    // Shift+Enter creates newline (default behavior)
  };

  const handleAttachClick = (type) => {
    setShowAttachMenu(false);
    switch (type) {
      case "camera":
        cameraInputRef.current?.click();
        break;
      case "image":
        imageInputRef.current?.click();
        break;
      case "file":
        fileInputRef.current?.click();
        break;
    }
  };

  const canSend = !disabled && !isLoading && (value.trim() || selectedFile || imagePreview);
  const isListening = voiceState === "listening";

  return (
    <div className="nexi-composer">
      {/* File/Image Preview */}
      <AnimatePresence>
        {(selectedFile || imagePreview) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="nexi-composer-preview"
          >
            {selectedFile && (
              <div className="nexi-file-preview">
                <div className="nexi-file-icon">
                  <FileText size={18} />
                </div>
                <div className="nexi-file-info">
                  <p className="nexi-file-name">{selectedFile.name}</p>
                  <p className="nexi-file-status">Ready to analyze</p>
                </div>
                <button onClick={onRemoveFile} className="nexi-remove-btn" data-testid="remove-file">
                  <X size={16} />
                </button>
              </div>
            )}

            {imagePreview && (
              <div className="nexi-image-preview">
                <img src={imagePreview} alt="Preview" />
                <button onClick={onRemoveImage} className="nexi-remove-img-btn" data-testid="remove-image">
                  <X size={12} />
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Input Container */}
      <div className={`nexi-input-container ${isFocused ? 'focused' : ''} ${isListening ? 'listening' : ''}`}>
        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.txt,.doc,.docx,.csv,.json,.md"
          onChange={(e) => onFileSelect?.(e.target.files?.[0])}
          className="hidden"
        />
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => onImageSelect?.(e.target.files?.[0])}
          className="hidden"
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => onCameraSelect?.(e.target.files?.[0])}
          className="hidden"
        />

        <div className="nexi-input-row">
          {/* Attach Button */}
          <div className="nexi-attach-wrapper">
            <button
              type="button"
              onClick={() => setShowAttachMenu(!showAttachMenu)}
              className={`nexi-attach-btn ${showAttachMenu ? 'active' : ''}`}
              data-testid="attach-button"
            >
              <Plus size={20} className={showAttachMenu ? 'rotate-45' : ''} />
            </button>

            {/* Attach Menu */}
            <AnimatePresence>
              {showAttachMenu && (
                <>
                  <div className="nexi-attach-overlay" onClick={() => setShowAttachMenu(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="nexi-attach-menu"
                  >
                    <button onClick={() => handleAttachClick("camera")} data-testid="attach-camera">
                      <Camera size={18} className="text-blue-400" />
                      <span>Camera</span>
                    </button>
                    <button onClick={() => handleAttachClick("image")} data-testid="attach-image">
                      <Image size={18} className="text-emerald-400" />
                      <span>Photo Library</span>
                    </button>
                    <button onClick={() => handleAttachClick("file")} data-testid="attach-file">
                      <FileText size={18} className="text-violet-400" />
                      <span>Document</span>
                    </button>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>

          {/* Text Input */}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={placeholder}
            disabled={disabled || isLoading}
            rows={1}
            className="nexi-textarea"
            data-testid="chat-input"
          />

          {/* Voice Button */}
          {voiceSupported && onVoiceClick && (
            <button
              type="button"
              onClick={onVoiceClick}
              className={`nexi-voice-btn ${isListening ? 'active' : ''}`}
              data-testid="voice-button"
            >
              {isListening ? <MicOff size={18} /> : <Mic size={18} />}
            </button>
          )}

          {/* Send Button */}
          <button
            type="button"
            onClick={onSend}
            disabled={!canSend}
            className={`nexi-send-btn ${canSend ? 'active' : ''}`}
            data-testid="send-button"
          >
            {isLoading ? (
              <div className="nexi-spinner" />
            ) : (
              <Send size={18} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NexiComposer;
